import React from "react";
import { ExternalLink, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MARKETPLACE, PLATFORM_LOGOS } from "@/data/treasures";
import { Eyebrow, SectionTitle } from "@/components/shared";
import { Ornament } from "@/components/Ornament";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-6 h-6" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Card = ({ href, children, label, sub, testid }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
    className="group flex items-center gap-4 rounded-2xl border border-gold/25 bg-cream px-6 py-5 transition-all duration-300 hover:border-gold hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(43,27,18,0.12)]">
    <div className="w-12 h-12 rounded-xl bg-sand flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-gold/15">{children}</div>
    <div className="flex-1 min-w-0">
      <span className="block text-sm font-medium text-espresso group-hover:text-gold-deep transition-colors truncate">{label}</span>
      {sub && <span className="block text-xs text-espresso/50">{sub}</span>}
    </div>
    <ExternalLink className="w-4 h-4 text-gold-deep group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
  </a>
);

export const ReviewButtons = ({ dark = false }) => {
  const { t, contact } = useLang();
  return (
    <div className="w-full" data-testid="review-buttons">
      <div className={dark ? "text-center" : ""}>
        <Eyebrow dark={dark}>{t.reviews.title}</Eyebrow>
        <p className={`mb-6 font-sans text-sm ${dark ? "text-cream/70" : "text-espresso/60"}`}>{t.reviews.subtitle}</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Card href={contact.googleReviews} label={t.reviews.google} sub="Google" testid="review-google">
          <GoogleG />
          <span className="sr-only">Google</span>
        </Card>
        <Card href={MARKETPLACE.tutti} label={t.platforms.tutti} sub="tutti.ch" testid="review-tutti">
          <img src={PLATFORM_LOGOS.tutti} alt="Tutti" className="w-9 h-9 object-contain" />
        </Card>
        <Card href={MARKETPLACE.ricardo} label={t.platforms.ricardo} sub="ricardo.ch" testid="review-ricardo">
          <img src={PLATFORM_LOGOS.ricardo} alt="Ricardo" className="w-9 h-9 object-contain" />
        </Card>
      </div>
    </div>
  );
};

// Compact star rating cue used near CTAs.
export const StarBadge = ({ text }) => (
  <span className="inline-flex items-center gap-1.5 text-gold text-sm">
    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}
    {text && <span className="ml-1 text-espresso/60 text-xs">{text}</span>}
  </span>
);

export default ReviewButtons;
